import {
	Card,
	Tabs,
	Descriptions,
	Tag,
	Button,
	Space,
	Typography,
	Row,
	Col,
	Spin,
	notification,
	Badge,
	Alert,
	Divider,
	Table,
	Progress,
	Tooltip,
	Modal,
	Form,
	Input,
	Popconfirm,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	PiHardDrivesDuotone,
	PiArrowLeftDuotone,
	PiPencilDuotone,
	PiGlobeDuotone,
	PiShieldCheckDuotone,
	PiTerminalDuotone,
	PiGearDuotone,
	PiClipboardDuotone,
	PiAppStoreLogoDuotone,
	PiCubeDuotone,
	PiDatabaseDuotone,
	PiLinkBreakDuotone,
} from "react-icons/pi";
import {
	requestAttachAPLAServerApplicationToInstance,
	requestAttachAPLAServerDatabaseToInstance,
	requestAttachAPLAServerServiceToInstance,
	requestAttachWEHOServerAccountToInstance,
	requestDetachWEHOServerAccountFromInstance,
	requestGetAPLAServerApplicationList,
	requestGetAPLAServerDatabaseList,
	requestGetAPLAServerServiceList,
	requestGetServerById,
	requestGetWEHOServerAccountList,
} from "../../services/admin.service";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

function ViewServerPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [attaching, setAttaching] = useState(false);
	const [server, setServer] = useState(null);
	const [activeTab, setActiveTab] = useState("details");
	const [accounts, setAccounts] = useState([]);
	const [applications, setApplications] = useState([]);
	const [databases, setDatabases] = useState([]);
	const [services, setServices] = useState([]);
	const [attachModalVisible, setAttachModalVisible] = useState(false);
	const [selectedType, setSelectedType] = useState("");
	const [selectedUsername, setSelectedUsername] = useState("");
	const [selectedUuid, setSelectedUuid] = useState("");
	const [attachForm] = Form.useForm();

	useEffect(() => {
		document.title = "Cloudiva Console | View Server";
		fetchServerDetails();
	}, [id]);

	const fetchServerDetails = () => {
		setLoading(true);
		requestGetServerById(id)
			.then((response) => {
				setServer(response.data.data);

				if (response.data?.data?.type === "WEHO") {
					fetchAccountList(id);
				} else if (response.data?.data?.type === "APLA") {
					fetchApplicationList(id);
					fetchDatabaseList(id);
					fetchServiceList(id);
				}
			})
			.catch((error) => {
				console.error("Error fetching server:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch server details",
				});
				navigate("/settings/servers");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchAccountList = (id) => {
		setLoading(true);
		requestGetWEHOServerAccountList(id)
			.then((response) => {
				setAccounts(response.data.data);
			})
			.catch((error) => {
				console.error("Error fetching server accounts:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch server accounts",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchApplicationList = (id) => {
		setLoading(true);
		requestGetAPLAServerApplicationList(id)
			.then((response) => {
				setApplications(response.data.data);
			})
			.catch((error) => {
				console.error("Error fetching server applications:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch server applications",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchDatabaseList = (id) => {
		setLoading(true);
		requestGetAPLAServerDatabaseList(id)
			.then((response) => {
				setDatabases(response.data.data);
			})
			.catch((error) => {
				console.error("Error fetching server databases:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch server databases",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchServiceList = (id) => {
		setLoading(true);
		requestGetAPLAServerServiceList(id)
			.then((response) => {
				setServices(response.data.data);
			})
			.catch((error) => {
				console.error("Error fetching server services:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch server services",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const attachAccountToInstance = (username) => {
		setSelectedType("account");
		setSelectedUsername(username);
		setAttachModalVisible(true);
		attachForm.resetFields();
	};

	const attachApplicationToInstance = (uuid) => {
		setSelectedType("application");
		setSelectedUuid(uuid);
		setAttachModalVisible(true);
		attachForm.resetFields();
	};

	const attachDatabaseToInstance = (uuid) => {
		setSelectedType("database");
		setSelectedUuid(uuid);
		setAttachModalVisible(true);
		attachForm.resetFields();
	};

	const attachServiceToInstance = (uuid) => {
		setSelectedType("service");
		setSelectedUuid(uuid);
		setAttachModalVisible(true);
		attachForm.resetFields();
	};

	const handleAttachSubmit = (values) => {
		setAttaching(true);
		if (selectedType === "account") {
			requestAttachWEHOServerAccountToInstance(id, {
				instanceId: values.instanceId,
				username: selectedUsername,
			})
				.then((response) => {
					notification.success({
						message: "Success",
						description: "Account attached to instance successfully",
					});
					setAttachModalVisible(false);
					setSelectedType("");
					setSelectedUsername("");
					setSelectedUuid("");
					attachForm.resetFields();
					fetchAccountList(id); // Refresh account list
				})
				.catch((error) => {
					console.error("Error attaching account:", error);
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to attach account to instance",
					});
				})
				.finally(() => {
					setAttaching(false);
				});
		} else if (selectedType === "application") {
			requestAttachAPLAServerApplicationToInstance(id, {
				instanceId: values.instanceId,
				uuid: selectedUuid,
			})
				.then((response) => {
					notification.success({
						message: "Success",
						description: "Application attached to instance successfully",
					});
					setAttachModalVisible(false);
					setSelectedType("");
					setSelectedUuid("");
					attachForm.resetFields();
					fetchApplicationList(id); // Refresh application list
				})
				.catch((error) => {
					console.error("Error attaching application:", error);
					notification.error({
						message: "Error",
						description:
							error.response?.data?.message || "Failed to attach application to instance",
					});
				})
				.finally(() => {
					setAttaching(false);
				});
		} else if (selectedType === "database") {
			requestAttachAPLAServerDatabaseToInstance(id, {
				instanceId: values.instanceId,
				uuid: selectedUuid,
			})
				.then((response) => {
					notification.success({
						message: "Success",
						description: "Database attached to instance successfully",
					});
					setAttachModalVisible(false);
					setSelectedType("");
					setSelectedUuid("");
					attachForm.resetFields();
					fetchDatabaseList(id); // Refresh database list
				})
				.catch((error) => {
					console.error("Error attaching database:", error);
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to attach database to instance",
					});
				})
				.finally(() => {
					setAttaching(false);
				});
		} else if (selectedType === "service") {
			requestAttachAPLAServerServiceToInstance(id, {
				instanceId: values.instanceId,
				uuid: selectedUuid,
			})
				.then((response) => {
					notification.success({
						message: "Success",
						description: "Service attached to instance successfully",
					});
					setAttachModalVisible(false);
					setSelectedType("");
					setSelectedUuid("");
					attachForm.resetFields();
					fetchServiceList(id); // Refresh service list
				})
				.catch((error) => {
					console.error("Error attaching service:", error);
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to attach service to instance",
					});
				})
				.finally(() => {
					setAttaching(false);
				});
		}
	};

	const detachAccountFromInstance = (accountId) => {
		setAttaching(true);
		requestDetachWEHOServerAccountFromInstance(id, accountId)
			.then((response) => {
				notification.success({
					message: "Success",
					description: "Account detached from instance successfully",
				});
				fetchAccountList(id); // Refresh account list
			})
			.catch((error) => {
				console.error("Error detaching account:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to detach account from instance",
				});
			})
			.finally(() => {
				setAttaching(false);
			});
	};

	const handleAttachCancel = () => {
		setAttachModalVisible(false);
		setSelectedUsername("");
		attachForm.resetFields();
	};

	const getServerTypeLabel = (type) => {
		const types = {
			WEHO: "Web Hosting",
			APLA: "App Launcher",
			GAME: "Game Server",
			VPS: "VPS",
			DEDI: "Dedicated Server",
		};
		return types[type] || type;
	};

	const getPanelLabel = (panel) => {
		const panels = {
			WEBUZO: "Webuzo",
			CWP: "Control Web Panel",
			COOLIFY: "Coolify",
			CLOUDPANEL: "Cloud Panel",
			CPANEL: "cPanel",
			PLESK: "Plesk",
			DIRECTADMIN: "DirectAdmin",
		};
		return panels[panel] || panel;
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "ACTIVE":
				return "green";
			case "MAINTENANCE":
				return "orange";
			case "INACTIVE":
				return "red";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "ACTIVE":
				return "Active";
			case "MAINTENANCE":
				return "Maintenance";
			case "INACTIVE":
				return "Inactive";
			default:
				return status;
		}
	};

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", marginTop: 50 }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!server) {
		return (
			<div className="page-content">
				<Alert
					message="Server not found"
					description="The server you're looking for doesn't exist."
					type="error"
					showIcon
				/>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/settings/servers")} />
						<Title level={4} style={{ margin: 0 }}>
							Server Details
						</Title>
						<Tag color={getStatusColor(server.status)}>{getStatusText(server.status)}</Tag>
					</Space>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card
					title={server.name}
					extra={
						<Button
							type="primary"
							icon={<PiPencilDuotone />}
							onClick={() => navigate(`/settings/servers/${server._id}/edit`)}>
							Edit Server
						</Button>
					}>
					<Tabs activeKey={activeTab} onChange={setActiveTab}>
						<TabPane
							tab={
								<Space>
									<PiHardDrivesDuotone />
									<span>Server Details</span>
								</Space>
							}
							key="details">
							<Row gutter={16}>
								<Col span={12}>
									<Card
										title={
											<Space>
												<PiGearDuotone />
												<span>Basic Information</span>
											</Space>
										}
										size="small">
										<Descriptions column={1} size="small">
											<Descriptions.Item label="Server Name">
												<Text strong>{server.name}</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Type">
												<Tag color="blue">{getServerTypeLabel(server.type)}</Tag>
											</Descriptions.Item>
											<Descriptions.Item label="Hostname">
												<Text code>{server.hostname}</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Region">
												<Space>
													<PiGlobeDuotone />
													<span>{server.region}</span>
												</Space>
											</Descriptions.Item>
											<Descriptions.Item label="Status">
												<Badge
													status={
														server.status === "ACTIVE"
															? "success"
															: server.status === "MAINTENANCE"
															? "warning"
															: "error"
													}
													text={getStatusText(server.status)}
												/>
											</Descriptions.Item>
											<Descriptions.Item label="Created At">
												{new Date(server.createdAt).toLocaleString()}
											</Descriptions.Item>
											{server.updatedAt && (
												<Descriptions.Item label="Last Updated">
													{new Date(server.updatedAt).toLocaleString()}
												</Descriptions.Item>
											)}
										</Descriptions>
									</Card>
								</Col>
								<Col span={12}>
									<Card
										title={
											<Space>
												<PiTerminalDuotone />
												<span>Connection Details</span>
											</Space>
										}
										size="small">
										<Descriptions column={1} size="small">
											<Descriptions.Item label="IP Address">
												<Text code>{server.ip}</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Port">
												<Text code>{server.port}</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Control Panel">
												<Tag color="green">{getPanelLabel(server.panel)}</Tag>
											</Descriptions.Item>
											<Descriptions.Item label="SSH Access">
												<Text code>
													ssh {server.credentials?.username}@{server.ip}
													{server.port && server.port !== 22 && ` -p ${server.port}`}
												</Text>
											</Descriptions.Item>
										</Descriptions>
									</Card>
								</Col>
							</Row>

							{server.description && (
								<>
									<Divider orientation="left">Description</Divider>
									<Card size="small">
										<Text>{server.description}</Text>
									</Card>
								</>
							)}
						</TabPane>

						<TabPane
							tab={
								<Space>
									<PiShieldCheckDuotone />
									<span>Credentials</span>
								</Space>
							}
							key="credentials">
							<Card
								title={
									<Space>
										<PiShieldCheckDuotone />
										<span>Access Credentials</span>
									</Space>
								}
								size="small">
								<Alert
									message="Sensitive Information"
									description="Please keep these credentials secure and do not share them."
									type="warning"
									showIcon
									style={{ marginBottom: 16 }}
								/>
								<Descriptions column={1} size="small">
									<Descriptions.Item label="Username">
										<Text code>{server.credentials?.username}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Password">
										<Text code>{server.credentials?.password ? "••••••••" : "Not set"}</Text>
									</Descriptions.Item>
									{server.credentials?.key && (
										<Descriptions.Item label="SSH Key">
											<Text code>SSH key configured</Text>
										</Descriptions.Item>
									)}
								</Descriptions>
							</Card>
						</TabPane>

						<TabPane
							tab={
								<Space>
									<PiGearDuotone />
									<span>System Info</span>
								</Space>
							}
							key="system">
							<Row gutter={16}>
								<Col span={8}>
									<Card title="Server Metrics" size="small">
										<Descriptions column={1} size="small">
											<Descriptions.Item label="Uptime">
												<Text>N/A</Text>
											</Descriptions.Item>
											<Descriptions.Item label="CPU Usage">
												<Text>N/A</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Memory Usage">
												<Text>N/A</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Disk Usage">
												<Text>N/A</Text>
											</Descriptions.Item>
										</Descriptions>
									</Card>
								</Col>
								<Col span={8}>
									<Card title="Network Info" size="small">
										<Descriptions column={1} size="small">
											<Descriptions.Item label="Bandwidth">
												<Text>N/A</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Data Transfer">
												<Text>N/A</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Network Speed">
												<Text>N/A</Text>
											</Descriptions.Item>
										</Descriptions>
									</Card>
								</Col>
								<Col span={8}>
									<Card title="Security" size="small">
										<Descriptions column={1} size="small">
											<Descriptions.Item label="Firewall Status">
												<Text>N/A</Text>
											</Descriptions.Item>
											<Descriptions.Item label="SSL Certificate">
												<Text>N/A</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Last Security Scan">
												<Text>N/A</Text>
											</Descriptions.Item>
										</Descriptions>
									</Card>
								</Col>
							</Row>
							<Alert
								message="System monitoring not implemented"
								description="Real-time system metrics and monitoring features will be available in future updates."
								type="info"
								showIcon
								style={{ marginTop: 16 }}
							/>
						</TabPane>

						{server.type === "WEHO" && (
							<TabPane
								tab={
									<Space>
										<PiClipboardDuotone />
										<span>Accounts</span>
									</Space>
								}
								key="accounts">
								<Card title="Server Accounts" size="small">
									<Table
										dataSource={accounts.map((account, index) => ({
											key: `account-${index}`,
											...account,
										}))}
										columns={[
											{
												title: "Username",
												dataIndex: "username",
												key: "username",
												render: (_, record) => {
													// Handle different panel types
													const username =
														server.panel === "WEBUZO"
															? record.metadata?.user || record.username
															: record.username ?? record.metadata?.username;
													return (
														<Text code copyable>
															{username}
														</Text>
													);
												},
											},
											{
												title: "Package",
												dataIndex: "package",
												key: "package",
												render: (_, record) => {
													// Handle different panel types
													if (server.panel === "WEBUZO") {
														return <Text>{record.metadata?.plan || "N/A"}</Text>;
													}
													return <Text>{record.metadata?.package_name || "N/A"}</Text>;
												},
											},
											{
												title: "Domain",
												dataIndex: "domain",
												key: "domain",
												render: (_, record) => (
													<Text code copyable>
														{record.metadata?.domain}
													</Text>
												),
											},
											{
												title: "Disk Usage",
												dataIndex: "diskUsage",
												key: "diskUsage",
												render: (_, record) => {
													if (server.panel === "WEBUZO") {
														const diskResource = record.metadata?.resource?.disk;
														if (
															!diskResource ||
															diskResource.limit === "0B" ||
															diskResource.limit_bytes === 0
														) {
															return <Text>Unlimited</Text>;
														}
														return (
															<Tooltip
																title={`Used: ${diskResource.used} / Limit: ${diskResource.limit}`}>
																<Progress
																	percent={diskResource.percent}
																	size="small"
																	status="active"
																	showInfo={false}
																/>
															</Tooltip>
														);
													}
													// CWP format
													const diskUsed = record.metadata?.diskused || 0;
													const diskLimit = record.metadata?.disklimit || 0;
													if (diskLimit === 0) {
														return <Text>Unlimited</Text>;
													}
													return (
														<Tooltip title={`Used: ${diskUsed} MB / Limit: ${diskLimit} MB`}>
															<Progress
																percent={(diskUsed / diskLimit) * 100}
																size="small"
																status="active"
																showInfo={false}
															/>
														</Tooltip>
													);
												},
											},
											{
												title: "Bandwidth",
												dataIndex: "bandwidth",
												key: "bandwidth",
												render: (_, record) => {
													if (server.panel === "WEBUZO") {
														const bwResource = record.metadata?.resource?.bandwidth;
														if (!bwResource || bwResource.limit === "unlimited") {
															return <Text>Unlimited</Text>;
														}
														return (
															<Tooltip
																title={`Used: ${bwResource.used} / Limit: ${bwResource.limit}`}>
																<Progress
																	percent={bwResource.percent}
																	size="small"
																	status="active"
																	showInfo={false}
																/>
															</Tooltip>
														);
													}
													// CWP format
													const bandwidth = record.metadata?.bandwidth || 0;
													const bwLimit = record.metadata?.bwlimit || 0;
													if (bwLimit === 0) {
														return <Text>Unlimited</Text>;
													}
													return (
														<Tooltip title={`Used: ${bandwidth} MB / Limit: ${bwLimit} MB`}>
															<Progress
																percent={(bandwidth / bwLimit) * 100}
																size="small"
																status="active"
																showInfo={false}
															/>
														</Tooltip>
													);
												},
											},
											{
												title: "Setup Date",
												dataIndex: "setupDate",
												key: "setupDate",
												render: (_, record) => {
													if (server.panel === "WEBUZO") {
														const created = record.metadata?.created;
														if (!created) return "N/A";
														return dayjs.unix(created).format("DD MMM YYYY HH:mm:ss");
													}
													// CWP format
													return record.metadata?.setup_date
														? dayjs(record.metadata.setup_date).format("DD MMM YYYY HH:mm:ss")
														: "N/A";
												},
											},
											{
												title: "Instance",
												dataIndex: "instance",
												key: "instance",
												align: "right",
												render: (_, record) => {
													const username =
														server.panel === "WEBUZO"
															? record.metadata?.user || record.username
															: record.metadata?.username || record.username;

													return record.instanceId ? (
														<Space>
															<Button
																type="dashed"
																onClick={() => navigate(`/instances/${record.instanceId}`)}>
																{record.instance.number}
															</Button>
															<Popconfirm
																title="Are you sure to detach this account?"
																placement="topRight"
																onConfirm={() => detachAccountFromInstance(record._id)}>
																<Button type="dashed" danger icon={<PiLinkBreakDuotone />}></Button>
															</Popconfirm>
														</Space>
													) : (
														<Button
															type="primary"
															onClick={() => attachAccountToInstance(username)}
															loading={attaching}>
															Attach
														</Button>
													);
												},
											},
										]}
										pagination={false}
									/>
								</Card>
							</TabPane>
						)}
						{/* for apla server */}
						{/* first. apla application list */}
						{server.type === "APLA" && (
							<Tabs.TabPane
								tab={
									<Space>
										<PiCubeDuotone />
										<span>Applications</span>
									</Space>
								}
								key="apla.applications">
								{/* Create application list like the above tab */}
								<Card title="All Applications" size="small">
									<Table
										dataSource={applications.map((app, index) => ({
											key: `app-${index}`,
											...app,
										}))}
										loading={loading}
										columns={[
											{
												title: "Name",
												dataIndex: ["metadata", "name"],
												key: "name",
												ellipsis: true,
												render: (text) => (
													<Typography.Text style={{ fontSize: "12px" }}>
														{text || "N/A"}
													</Typography.Text>
												),
											},
											{
												title: "UUID",
												dataIndex: ["metadata", "uuid"],
												key: "uuid",
												width: 120,
												render: (text) => (
													<span style={{ fontSize: "11px", fontFamily: "monospace" }}>
														{text ? `${text.substring(0, 8)}...` : "N/A"}
													</span>
												),
											},
											{
												title: "Status",
												dataIndex: ["metadata", "status"],
												key: "status",
												width: 120,
												render: (status) => {
													if (!status) return <span>N/A</span>;
													const [state, health] = status.split(":");
													const statusColor =
														state === "running"
															? health === "healthy"
																? "success"
																: "warning"
															: "error";
													return (
														<Tag
															style={{
																fontSize: "12px",
																fontWeight: "500",
															}}
															color={statusColor}>
															{status}
														</Tag>
													);
												},
											},
											{
												title: "Repository",
												dataIndex: ["metadata", "git_repository"],
												key: "repository",
												width: 150,
												render: (text) => <span style={{ fontSize: "12px" }}>{text || "N/A"}</span>,
											},
											{
												title: "Branch",
												dataIndex: ["metadata", "git_branch"],
												key: "branch",
												width: 80,
												render: (text) => (
													<span style={{ fontSize: "11px", fontFamily: "monospace" }}>
														{text || "N/A"}
													</span>
												),
											},
											{
												title: "FQDN",
												dataIndex: ["metadata", "fqdn"],
												key: "fqdn",
												width: 200,
												render: (fqdn) => {
													if (!fqdn) return <span>N/A</span>;
													const domains = fqdn.split(",");
													return (
														<div>
															{domains.slice(0, 2).map((domain, idx) => (
																<div key={idx} style={{ fontSize: "11px" }}>
																	<a
																		href={domain}
																		target="_blank"
																		rel="noopener noreferrer"
																		style={{ color: "#1890ff" }}>
																		{domain.replace("https://", "").replace("http://", "")}
																	</a>
																</div>
															))}
															{domains.length > 2 && (
																<span style={{ fontSize: "10px", color: "#999" }}>
																	+{domains.length - 2} more
																</span>
															)}
														</div>
													);
												},
											},
											// {
											// 	title: "Env",
											// 	dataIndex: ["metadata", "environment_id"],
											// 	key: "environment",
											// 	width: 80,
											// 	render: (text) => (
											// 		<span style={{ fontSize: "11px" }}>ENV-{text || "N/A"}</span>
											// 	),
											// },
											{
												title: "Last Online",
												dataIndex: ["metadata", "last_online_at"],
												key: "lastOnline",
												ellipsis: true,
												width: 130,
												render: (text, record) => {
													if (!text) return <span>N/A</span>;
													const date = new Date(text);
													const createdAt = new Date(record.metadata.created_at);
													return (
														<Space direction="vertical">
															<span style={{ fontSize: "11px" }}>
																{date.toLocaleDateString()}{" "}
																{date.toLocaleTimeString([], {
																	hour: "2-digit",
																	minute: "2-digit",
																})}
															</span>
															<span style={{ fontSize: "11px", color: "#999" }}>
																Started: {createdAt.toLocaleDateString()}{" "}
																{createdAt.toLocaleTimeString([], {
																	hour: "2-digit",
																	minute: "2-digit",
																})}
															</span>
														</Space>
													);
												},
											},
											{
												title: "Instance",
												dataIndex: "instance",
												key: "instance",
												align: "right",
												render: (_, record) =>
													record.instanceId ? (
														<Button
															type="dashed"
															onClick={() => navigate(`/instances/${record.instanceId}`)}>
															{record.instance.number}
														</Button>
													) : (
														<Button
															type="primary"
															onClick={() => attachApplicationToInstance(record.metadata.uuid)}
															loading={attaching}>
															Attach
														</Button>
													),
											},
										]}
									/>
								</Card>
							</Tabs.TabPane>
						)}

						{/* Databases Tab */}
						{server.type === "APLA" && (
							<Tabs.TabPane
								tab={
									<Space>
										<PiDatabaseDuotone />
										<span>Databases</span>
									</Space>
								}
								key="apla.databases">
								<Card title="All Databases" size="small">
									<Table
										dataSource={databases.map((db, index) => ({
											key: `db-${index}`,
											...db,
										}))}
										loading={loading}
										columns={[
											{
												title: "Database Name",
												dataIndex: ["metadata", "name"],
												key: "name",
												ellipsis: true,
												render: (text) => (
													<Typography.Text style={{ fontSize: "12px" }}>
														{text || "N/A"}
													</Typography.Text>
												),
											},
											{
												title: "UUID",
												dataIndex: ["metadata", "uuid"],
												key: "uuid",
												width: 120,
												render: (text) => (
													<span style={{ fontSize: "11px", fontFamily: "monospace" }}>
														{text ? `${text.substring(0, 8)}...` : "N/A"}
													</span>
												),
											},
											{
												title: "Type",
												dataIndex: ["metadata", "database_type"],
												key: "type",
												width: 140,
												render: (text) => {
													if (!text) return <span>N/A</span>;
													const type = text.replace("standalone-", "").toUpperCase();
													return (
														<span
															style={{
																fontSize: "11px",
																fontWeight: "500",
																color: "#1890ff",
															}}>
															{type}
														</span>
													);
												},
											},
											{
												title: "Status",
												dataIndex: ["metadata", "status"],
												key: "status",
												width: 120,
												render: (status) => {
													if (!status) return <span>N/A</span>;
													const [state, health] = status.split(":");
													const statusColor =
														state === "running"
															? health === "healthy"
																? "success"
																: "warning"
															: "error";
													return (
														<Tag
															style={{
																fontSize: "12px",
																fontWeight: "500",
															}}
															color={statusColor}>
															{status}
														</Tag>
													);
												},
											},
											{
												title: "Image",
												dataIndex: ["metadata", "image"],
												key: "image",
												width: 120,
												render: (text) => (
													<span style={{ fontSize: "11px", fontFamily: "monospace" }}>
														{text || "N/A"}
													</span>
												),
											},
											{
												title: "Port",
												dataIndex: ["metadata", "ports_mappings"],
												key: "ports",
												width: 120,
												render: (text) => (
													<span style={{ fontSize: "11px", fontFamily: "monospace" }}>
														{text || "N/A"}
													</span>
												),
											},
											// {
											// 	title: "Public Port",
											// 	dataIndex: ["metadata", "public_port"],
											// 	key: "publicPort",
											// 	width: 100,
											// 	render: (text) => (
											// 		<span
											// 			style={{
											// 				fontSize: "11px",
											// 				fontFamily: "monospace",
											// 				color: "#f56a00",
											// 				fontWeight: "500",
											// 			}}>
											// 			{text || "N/A"}
											// 		</span>
											// 	),
											// },
											// {
											// 	title: "Environment",
											// 	dataIndex: ["metadata", "environment_id"],
											// 	key: "environment",
											// 	width: 80,
											// 	render: (text) => (
											// 		<span style={{ fontSize: "11px" }}>ENV-{text || "N/A"}</span>
											// 	),
											// },
											{
												title: "Last Online",
												dataIndex: ["metadata", "last_online_at"],
												key: "lastOnline",
												ellipsis: true,
												width: 130,
												render: (text, record) => {
													if (!text) return <span>N/A</span>;
													const date = new Date(text);
													const createdAt = new Date(record.metadata.created_at);
													return (
														<Space direction="vertical">
															<span style={{ fontSize: "11px" }}>
																{date.toLocaleDateString()}{" "}
																{date.toLocaleTimeString([], {
																	hour: "2-digit",
																	minute: "2-digit",
																})}
															</span>
															<span style={{ fontSize: "11px", color: "#999" }}>
																Started: {createdAt.toLocaleDateString()}{" "}
																{createdAt.toLocaleTimeString([], {
																	hour: "2-digit",
																	minute: "2-digit",
																})}
															</span>
														</Space>
													);
												},
											},
											{
												title: "Instance",
												dataIndex: "instance",
												key: "instance",
												align: "right",
												render: (_, record) =>
													record.instanceId ? (
														<Button
															type="dashed"
															onClick={() => navigate(`/instances/${record.instanceId}`)}>
															{record.instance.number}
														</Button>
													) : (
														<Button
															type="primary"
															onClick={() => attachDatabaseToInstance(record.metadata.uuid)}
															loading={attaching}>
															Attach
														</Button>
													),
											},
										]}
									/>
								</Card>
							</Tabs.TabPane>
						)}

						{/* Service Tab */}
						{server.type === "APLA" && (
							<Tabs.TabPane
								tab={
									<Space>
										<PiGearDuotone />
										<span>Services</span>
									</Space>
								}
								key="apla.services">
								<Card
									title="All Services"
									size="small"
									extra={
										<Button type="primary" onClick={() => fetchServiceList(id)} loading={loading}>
											Refresh Services
										</Button>
									}>
									<Table
										dataSource={services.map((service, index) => ({
											key: `service-${index}`,
											...service,
										}))}
										loading={loading}
										columns={[
											{
												title: "Name",
												dataIndex: ["metadata", "name"],
												key: "name",
												ellipsis: true,
												render: (text) => (
													<Typography.Text style={{ fontSize: "12px" }}>
														{text || "N/A"}
													</Typography.Text>
												),
											},
											{
												title: "UUID",
												dataIndex: ["metadata", "uuid"],
												key: "uuid",
												render: (text) => (
													<Typography.Text style={{ fontSize: "12px" }} copyable>
														{text || "N/A"}
													</Typography.Text>
												),
											},
											{
												title: "Type",
												dataIndex: ["metadata", "service_type"],
												key: "service_type",
												render: (text) => text || "-",
											},
											{
												title: "Status",
												dataIndex: ["metadata", "status"],
												key: "status",
												render: (status) => {
													if (!status) return <span>N/A</span>;
													const [state, health] = status.split(":");
													const statusColor =
														state === "running"
															? health === "healthy"
																? "success"
																: "warning"
															: "error";
													return (
														<Tag
															style={{
																fontSize: "12px",
																fontWeight: "500",
															}}
															color={statusColor}>
															{status}
														</Tag>
													);
												},
											},
											{
												title: "Server Status",
												dataIndex: ["metadata", "server_status"],
												key: "server_status",
												render: (text) => (
													<Tag color={text ? "green" : "red"}>{text ? "Online" : "Offline"}</Tag>
												),
											},
											// {
											// 	title: "Environment ID",
											// 	dataIndex: ["metadata", "environment_id"],
											// 	key: "environment_id",
											// 	width: 80,
											// 	render: (text) => text || "N/A",
											// },
											{
												title: "Created At",
												dataIndex: ["metadata", "created_at"],
												key: "created_at",
												render: (text) => (text ? dayjs(text).format("YYYY-MM-DD HH:mm") : "N/A"),
											},
											{
												title: "Instance",
												dataIndex: "instance",
												key: "instance",
												align: "right",
												render: (_, record) =>
													record.instanceId ? (
														<Button
															type="dashed"
															onClick={() => navigate(`/instances/${record.instanceId}`)}>
															{record.instance.number}
														</Button>
													) : (
														<Button
															type="primary"
															onClick={() => attachServiceToInstance(record.metadata.uuid)}
															loading={attaching}>
															Attach
														</Button>
													),
											},
										]}
									/>
								</Card>
							</Tabs.TabPane>
						)}
					</Tabs>
				</Card>
			</div>

			{/* Attach Modal */}
			<Modal
				title="Attach to Instance"
				open={attachModalVisible}
				onCancel={handleAttachCancel}
				footer={null}
				width={500}>
				<Form
					form={attachForm}
					layout="vertical"
					onFinish={handleAttachSubmit}
					style={{ marginTop: 16 }}>
					<Alert
						message={`Attaching ${
							selectedType === "account"
								? "account"
								: selectedType === "application"
								? "application"
								: selectedType === "database"
								? "database"
								: selectedType === "service"
								? "service"
								: ""
						}: ${selectedUsername || selectedUuid}`}
						type="info"
						showIcon
						style={{ marginBottom: 16 }}
					/>

					<Form.Item
						label="Instance ID"
						name="instanceId"
						rules={[
							{ required: true, message: "Please enter an instance ID" },
							{ min: 24, max: 24, message: "Instance ID must be exactly 24 characters" },
						]}
						extra="Enter the full Instance ID (24 characters)">
						<Input placeholder="e.g., 688cb57c8bb88f5ab0cda012" maxLength={24} />
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleAttachCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={attaching}>
								Attach Account
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default ViewServerPage;
